import { Back, Elastic, Bounce } from "gsap";
import { IReelConfig } from "../../../core/reel/IData/IReelData";
import { IWinRatios } from "./IWinConfig";
import { CustomEase } from "gsap/dist/CustomEase";
import { ReelSetBuilder } from "../../../core/utils/ReelSetBuilder"
import { ISymbolDim } from "../../../core/reel/symbol/config/IReelSymbolStruct";
import { GameMode } from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";

interface IGameDim {
    readonly landscape:{
        WIDTH: number;
        HEIGHT: number;
    };
    readonly portrait:{
        WIDTH: number;
        HEIGHT: number;
    };
}

class IBaseConfig {
    public static readonly BASE_DIM: IGameDim;
    public static readonly DEFAULT_SYMBOL_DIM: ISymbolDim;
    public static readonly REEL_ENGINE_CONFIG: IReelConfig;
    public static readonly WIN_RATIOS: IWinRatios;
    public static readonly NO_WIN_GAME_DATA:any;
}

export class SanGameMode extends GameMode {
    static readonly FREESPIN_SETUP1:string = "FREESPIN_SETUP1";
    static readonly FREESPIN_SETUP2:string = "FREESPIN_SETUP2";
}

export class GameConfig implements IBaseConfig {

    // public static instance:GameConfig = new GameConfig();
    public static readonly BASE_DIM: IGameDim = {
        landscape: {
            WIDTH: 1280, HEIGHT: 720
        },
        portrait: {
            WIDTH: 600, HEIGHT: 720
        }
    }

    public static readonly DEFAULT_SYMBOL_DIM: ISymbolDim = { 
        WIDTH: 136,
        HEIGHT: 148,
        PADDING_X: 4,
        PADDING_Y: 0,
        SCALE: new PIXI.Point(1, 1)
    };

    public static readonly SPLIT_OFFSET:number = 0.075;
       
    static SPEED_FACTOR_SPIN = 1.25;
    // **  Reel Config ** //
    public static readonly REEL_ENGINE_CONFIG: IReelConfig = {

        // Creates Overflow Symbols.
        OVERFLOW: true,

        // How many symbols should be displayed as part of the winArea and how many reels there are.
        WINDOW_AMOUNT: 4,

        INDIVIDUAL_MASK:false,

        // How many extra Symbols will be added for the stop reel. Increasing the distance for the reel to stop which simplifies other easing stops.
        STOP_DISTANCE: 0,

        // Define Reelsets for the reels to use.
        REEL_SETS: ReelSetBuilder.createSpinReels({
            amountOfReels: 5,
            maxVisibleSize: 4,
            minimumSize:50,
            fillerSymbols: {
                amount: 4,
                type: [ "L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"],
                maxAmountInView:1
                }
            },
            [
                {
                    name: "NORMAL",
                    symbols: [
                        { type: "S", amount: [0,1,1,1,1,0], excludeWithSymbol: ["W"], maxAmountInView: 1 },
                        { type: "W", amount: [0,3,3,3,3,0], excludeWithSymbol: ["S"], maxAmountInView: 1 },
                    ]
                },{
                    name: "FREESPIN_SETUP2",
                    fillerReplace:["X"],
                    symbols: [ 
                        { type: "Z", amount: 10, excludeWithSymbol: [], maxAmountInView: 3 },
                    ]
                },{
                    name: "FREESPIN_SETUP1",
                    fillerReplace:["X"],
                    symbols: [ 
                        { type: "Z", amount: 10, excludeWithSymbol: [], maxAmountInView: 3 },
                    ]
                },
                {
                    name: "FREESPIN",
                    symbols: []
                }
            ]
        ),
        
        DIM: {
            PADDING_X: 4, 
            PADDING_Y: 0,
            WIDTH: 136,
            HEIGHT: 148*4 // 148 * 4
        },
        // Define the duration for specific states.
        DURATION: {

            START:      0.4 / this.SPEED_FACTOR_SPIN,
            FAST_START: 0.2 / this.SPEED_FACTOR_SPIN,
            QUICK_START: 0.4 / this.SPEED_FACTOR_SPIN,
            SPIN:       0.07 / this.SPEED_FACTOR_SPIN, 
            FAST_SPIN: 0.04 / this.SPEED_FACTOR_SPIN,
            STOP:       0.6 / this.SPEED_FACTOR_SPIN,
            FAST_STOP:  0.25 / this.SPEED_FACTOR_SPIN,
            QUICK_STOP: 0.1 / this.SPEED_FACTOR_SPIN,
            NEAR_WIN:   0.03 / this.SPEED_FACTOR_SPIN,

            ATTENTION_STOP: 1.8/ this.SPEED_FACTOR_SPIN,

            EXPAND: 0.5 /  this.SPEED_FACTOR_SPIN
        },
        // Define the delay for specific states.
        DELAY: {
            START: 0.045 / this.SPEED_FACTOR_SPIN,
            FAST_START: 0 / this.SPEED_FACTOR_SPIN,
            STOP: 0.15 / this.SPEED_FACTOR_SPIN,
            FAST_STOP: 0.03 / this.SPEED_FACTOR_SPIN,
            QUICK_STOP: 0.025 / this.SPEED_FACTOR_SPIN,
        },
        // Define the Easeing for specific states.
        EASING: {

            START: Back.easeIn.config(3),
            FAST_START: Back.easeIn.config(1.25),

            STOP: CustomEase.create("stop", "M0,0 C0.166,0.222 0.373,0.502 0.484,0.634 0.586,0.756 0.748,0.946 0.8,1 0.848,1.028 0.9,1 0.9,1 0.956,0.988 0.944,1 1,1"),
            START_TEASER: CustomEase.create("teaserStart", "M0,0,C0,0,0.057,-0.003,0.09,-0.012,0.177,-0.037,0.231,-0.065,0.32,-0.092,0.355,-0.103,0.379,-0.106,0.415,-0.109,0.437,-0.111,0.453,-0.109,0.475,-0.106,0.495,-0.102,0.51,-0.099,0.527,-0.089,0.563,-0.069,0.589,-0.047,0.622,-0.022,0.65,0,0.7,0.042,0.776,0.042,0.836,0.042,0.877,0.019,0.9,0,0.942,-0.036,1,0,1,0"),

            FAST_STOP: Back.easeOut.config(0.7),
            QUICK_STOP: CustomEase.create("quick_stop", "M0,0 C0.132,0.242 0.328,0.536 0.432,0.676 0.526,0.803 0.7,1 0.7,1 0.814,1.05 0.846,1 0.846,1 0.878,0.98 0.926,1 0.926,1 0.954,1.01 0.962,1 1,1 "),// Elastic.easeOut.config(0.52, 0.32),

            ATTENTION_STOP: Back.easeOut.config(0.2),

            CHOKO_START: Back.easeIn.config(1.6),
            CHOKO_STOP: Back.easeOut.config(1.6),
            // FAST_STOP: Elastic.easeOut.config(0.8, 0.55),
            // QUICK_STOP:  Elastic.easeOut.config(0.8, 0.55),
            
            EXPAND: Elastic.easeOut.config(1, 1.1),
            NUDGE: CustomEase.create("nudge", "M0,0 C0,0 0.264,-0.156 0.3,0 0.321,0.084 0.559,0.91 0.6,1 0.628,1.062 0.737,1.02 0.8,1 0.88,0.974 1,1 1,1"),
        }
    }

    public static readonly WIN_RATIOS: IWinRatios = {
        normalWin: [
            {
                ratio: 0,
                text: "Tiny Win",
                duration: 0.1,
            },
            {
                ratio: 1.01,
                text: "Small Win",
                duration: 0.25,
            },
            {
                ratio: 3,
                text: "Medium Win",
                duration: 0.5,
            },
            {
                ratio: 5,
                text: "Large Win",
                duration: 1,
            }
        ],
        bigWin: [
            {
                ratio: 15,
                text: "Big Win",
                duration: 4,
            },
            {
                ratio: 25,
                text: "Super Win",
                duration: 4,
            },
            {
                ratio: 50,
                text: "Mega Win",
                duration: 8,
            },
            {
                ratio: 100,
                text: "Epic Win",
                duration: 8,
            },
            {
                ratio: 250,
                text: "Wicked Win",
                duration: 8,
            },
            {
                ratio: 1000,
                text: "Insanity Win",
                duration: 8,
            },
            {
                ratio: 10000,
                text: "Beyond Saving Win",
                duration: 8,
            }
        ]
    };
}